// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay from "@insite/content-library/Components/AddressInfoDisplay";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { RadioGroup, TextField } from "@insite/mobius/index";
import Radio from "@insite/mobius/Radio";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FieldInfoTooltip from "@werner/App/Components/Common/FieldInfoTooltip";
import Notify from "@werner/App/Components/Notify/Notify";
import updateXCheckoutState from "@werner/App/Handlers/Checkout/UpdateLoacalState";
import { CustomAppState } from "@werner/App/Store/Reducers";
import { selectHasOutOfStockItems } from "@werner/App/Store/Selectors/CartSelectors";
import { isRestrictedToShipwhenComplete } from "@werner/App/Store/Selectors/GeneralSelectors";
import { CHECKOUT_PICKUP_DAY, CHECKOUT_WHEN_TO_SHIP } from "@werner/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { xScreen } from "@werner/XDL/Utilities";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomAppState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const fulfillmentMethod = cartState.value?.fulfillmentMethod || getSession(state).fulfillmentMethod;
    return {
        cart: cartState.value,
        shippingPageState: state.xCheckout.shipping,
        isPickUp: fulfillmentMethod === FulfillmentMethod.PickUp,
        pickUpWarehouse: state.context.session.pickUpWarehouse,
        cartHasOutOfStockItems: selectHasOutOfStockItems(state),
        // XNG-changes
        isRestrictedToShipwhenComplete: isRestrictedToShipwhenComplete(state),
    };
};

const mapDispatchToProps = {
    updateXCheckoutState,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface PageStructureStyle {
    leftSideGridItem?: GridItemProps;
    rightSideGridItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    innerGridItem?: GridItemProps;
    notifyStyle?: TypographyPresentationProps;
}

export interface CheckoutShippingStepStyles {
    container?: GridContainerProps;
    centeringWrapper?: InjectableCss;
    shippingStyles?: PageStructureStyle;
    pickupStyles?: PageStructureStyle;
    addressTitle?: TypographyPresentationProps;
}

export const checkoutShippingStepStyles: CheckoutShippingStepStyles = {
    container: { gap: 20 },
    shippingStyles: {
        leftSideGridItem: {
            width: [12, 12, 12, 6, 6],
        },
        rightSideGridItem: {
            width: [12, 12, 12, 5, 5],
        },
        innerContainer: {
            gap: 10,
        },
        innerGridItem: {
            width: 12,
        },
    },
    pickupStyles: {
        leftSideGridItem: {
            width: [12, 12, 12, 6, 6],
            css: css`
                ${xScreen("gte", "lg")} {
                    padding-right: 50px;
                }
            `,
        },
        rightSideGridItem: {
            width: [12, 12, 12, 6, 6],
        },
        innerContainer: {
            gap: 30,
        },
        innerGridItem: {
            width: 12,
            css: css`
                flex-direction: column;
            `,
        },
        notifyStyle: {
            css: css`
                ${xScreen("gte", "lg")} {
                    margin-top: 20px;
                    max-width: 350px;
                }
            `,
        },
    },
    addressTitle: {
        size: 15,
        weight: 500,
        css: css`
            letter-spacing: 0;
            line-height: 17px;
            margin-bottom: 10px;
        `,
    },
};

const styles = checkoutShippingStepStyles;

const CheckoutShippingStep: FC<Props> = ({
    cart,
    updateXCheckoutState,
    shippingPageState,
    isPickUp,
    pickUpWarehouse,
    cartHasOutOfStockItems,
    isRestrictedToShipwhenComplete,
}) => {
    useEffect(() => {
        if (!cartHasOutOfStockItems) {
            return;
        }

        updateXCheckoutState({
            shipping: {
                pickupDay: CHECKOUT_PICKUP_DAY.Tomorrow,
            },
        });
    }, [cartHasOutOfStockItems]);

    useEffect(() => {
        if (!isRestrictedToShipwhenComplete) {
            return;
        }

        updateXCheckoutState({
            shipping: {
                shippingMethod: CHECKOUT_WHEN_TO_SHIP.ShipWhenComplete,
            },
        });
    }, [isRestrictedToShipwhenComplete]);

    const handleChangeAttention: React.ChangeEventHandler<HTMLInputElement> = e => {
        updateXCheckoutState({
            shipping: {
                attentionTo: e.target.value,
            },
        });
    };

    const handleChangeSiteContactPhoneNumber: React.ChangeEventHandler<HTMLInputElement> = e => {
        updateXCheckoutState({
            shipping: {
                contactPhoneNumber: e.target.value,
            },
        });
    };

    const handleWhenToShipChange = (e: any) => {
        updateXCheckoutState({
            shipping: {
                shippingMethod: e.target.value,
            },
        });
    };
    const handlePickupDayChange = (e: any) => {
        updateXCheckoutState({
            shipping: {
                pickupDay: e.target.value,
            },
        });
    };

    if (isPickUp) {
        return (
            <GridContainer {...styles.container}>
                <GridItem {...styles.pickupStyles?.leftSideGridItem}>
                    <GridContainer {...styles.pickupStyles?.innerContainer}>
                        <GridItem {...styles.pickupStyles?.innerGridItem}>
                            <Typography {...styles.addressTitle}>{translate("Warehouse Location")}</Typography>
                            <AddressInfoDisplay
                                address1={pickUpWarehouse?.address1 || ""}
                                address2={pickUpWarehouse?.address2}
                                city={pickUpWarehouse?.city || ""}
                                postalCode={pickUpWarehouse?.postalCode || ""}
                                state={pickUpWarehouse?.state}
                                phone={pickUpWarehouse?.phone}
                            />
                        </GridItem>
                        <GridItem {...styles.pickupStyles?.innerGridItem}>
                            {createWidgetElement("CheckoutShipping/CheckoutShippingOrderNotesEntry", {
                                fields: {},
                            })}
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem {...styles.pickupStyles?.rightSideGridItem}>
                    <GridContainer {...styles.pickupStyles?.innerContainer}>
                        <GridItem {...styles.pickupStyles?.innerGridItem} data-test-selector="checkoutShippingNotes">
                            <RadioGroup
                                label={translate("Pickup Day")}
                                value={shippingPageState.pickupDay}
                                onChange={handlePickupDayChange}
                                horizontal={true}
                            >
                                <Radio disabled={cartHasOutOfStockItems} value={CHECKOUT_PICKUP_DAY.Today}>
                                    {translate(CHECKOUT_PICKUP_DAY.Today)}
                                </Radio>
                                <Radio value={CHECKOUT_PICKUP_DAY.Tomorrow}>
                                    {translate(CHECKOUT_PICKUP_DAY.Tomorrow)}
                                </Radio>
                            </RadioGroup>
                        </GridItem>
                        <GridItem {...styles.pickupStyles?.innerGridItem} data-test-selector="checkoutShippingNotes">
                            <Notify type="warning" showClose={false} {...styles.pickupStyles?.notifyStyle}>
                                {shippingPageState.pickupDay === CHECKOUT_PICKUP_DAY.Today
                                    ? siteMessage("Werner_Checkout_PickupStep2TodayMessage")
                                    : siteMessage("Werner_Checkout_PickupStep2TomorrowMessage")}
                            </Notify>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        );
    }

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.shippingStyles?.leftSideGridItem}>
                <RadioGroup
                    label={translate("Shipping Method")}
                    value={shippingPageState.shippingMethod}
                    onChange={handleWhenToShipChange}
                >
                    {!isRestrictedToShipwhenComplete && (
                        <Radio value={CHECKOUT_WHEN_TO_SHIP.ShipWhenAvailable}>
                            {translate(CHECKOUT_WHEN_TO_SHIP.ShipWhenAvailable)}
                            &nbsp; &nbsp;
                            <FieldInfoTooltip message="Werner_Checkout_ShippingMethodShipWhenAvailableMsg" />
                        </Radio>
                    )}
                    <Radio value={CHECKOUT_WHEN_TO_SHIP.ShipWhenComplete}>
                        {translate(CHECKOUT_WHEN_TO_SHIP.ShipWhenComplete)}
                        &nbsp; &nbsp;
                        <FieldInfoTooltip message="Werner_Checkout_ShippingMethodShipWhenCompleteMsg" />
                    </Radio>
                </RadioGroup>
            </GridItem>
            <GridItem {...styles.shippingStyles?.rightSideGridItem}>
                <GridContainer {...styles.shippingStyles?.innerContainer}>
                    <GridItem {...styles.shippingStyles?.innerGridItem} data-test-selector="checkoutShippingNotes">
                        <TextField
                            placeholder={translate("Contact Name")}
                            label={translate("Site Contact Name")}
                            onChange={handleChangeAttention}
                            value={shippingPageState.attentionTo || ""}
                            required={false}
                        />
                    </GridItem>
                    <GridItem {...styles.shippingStyles?.innerGridItem} data-test-selector="checkoutShippingNotes">
                        <TextField
                            placeholder={translate("Phone Number")}
                            label={translate("Site Contact Phone Number (Optional)")}
                            onChange={handleChangeSiteContactPhoneNumber}
                            value={shippingPageState.contactPhoneNumber || ""}
                            required={false}
                            type="phone"
                            {...{ formId: "siteContactPhoneNumber" }}
                        />
                    </GridItem>
                    <GridItem {...styles.shippingStyles?.innerGridItem} data-test-selector="checkoutShippingNotes">
                        {createWidgetElement("CheckoutShipping/CheckoutShippingOrderNotesEntry", {
                            fields: {},
                        })}
                    </GridItem>
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutShippingStep),
    definition: {
        displayName: "Checkout Shipping Step",
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
    },
};

export default widgetModule;
